import React from 'react';
import classNames from 'classnames';
import Icon from 'src/components/Icon';
import styles from '../styles.module.scss';
import { ExtractionValueType } from './schemaModel';
import { EditorActions } from './editorTypes';
import FieldEditorCard from './FieldEditorCard';

interface FieldRowProps {
    pathId: string;
    fieldId: string;
    propertyKey: string;
    serializedObjectPath: string;
    serializedPropertyPath: string;
    displayName: string;
    title: string;
    description: string;
    summary: string;
    valueType: ExtractionValueType;
    arrayDepth: number;
    required: boolean;
    isGroup: boolean;
    isExpanded: boolean;
    isActive: boolean;
    isFiltering: boolean;
    canMoveUp: boolean;
    canMoveDown: boolean;
    editable: boolean;
    actions: EditorActions;
    children?: React.ReactNode;
}

function FieldRow({
    pathId,
    fieldId,
    propertyKey,
    serializedObjectPath,
    serializedPropertyPath,
    displayName,
    title,
    description,
    summary,
    valueType,
    arrayDepth,
    required,
    isGroup,
    isExpanded,
    isActive,
    isFiltering,
    canMoveUp,
    canMoveDown,
    editable,
    actions,
    children,
}: FieldRowProps) {
    const toggleEditor = () => {
        if (isActive) {
            actions.closeEditor();
        } else {
            actions.openEditor(fieldId);
        }
    };
    const rowHeading = (
        <span className={styles.extractionSchemaRowHeading}>
            <strong>{displayName}</strong>
            <code>{propertyKey}</code>
            <small className={styles.extractionSchemaRowSummary} data-test-id={`extraction-schema-summary-${pathId}`}>
                {summary}
            </small>
            {required ? <small className={styles.extractionSchemaRequiredBadge}>Required</small> : null}
            {!editable && description ? (
                <small className={styles.extractionSchemaRowDescription}>{description}</small>
            ) : null}
        </span>
    );

    return (
        <li className={styles.extractionSchemaProperty} data-test-id={`extraction-schema-property-${pathId}`}>
            <div
                className={classNames(styles.extractionSchemaRow, {
                    [styles.extractionSchemaRowActive]: isActive,
                    [styles.extractionSchemaRowStatic]: !editable,
                    [styles.extractionSchemaGroupRow]: isGroup,
                })}
                data-test-id={`extraction-schema-row-${pathId}`}
            >
                {isGroup ? (
                    <button
                        type="button"
                        className={styles.extractionSchemaGroupToggle}
                        onClick={(event) => {
                            event.stopPropagation();
                            actions.toggleGroup(fieldId);
                        }}
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${displayName}`}
                        data-test-id={`extraction-schema-group-toggle-${pathId}`}
                    >
                        <Icon type="fa" name={isExpanded ? 'chevron-down' : 'chevron-right'} aria-hidden="true" />
                    </button>
                ) : (
                    <span className={styles.extractionSchemaRowSpacer} aria-hidden="true" />
                )}
                {editable ? (
                    <button
                        type="button"
                        className={styles.extractionSchemaRowMain}
                        aria-expanded={isActive}
                        onClick={toggleEditor}
                        data-test-id={`extraction-schema-open-${pathId}`}
                    >
                        {rowHeading}
                    </button>
                ) : (
                    <span className={styles.extractionSchemaRowMain}>{rowHeading}</span>
                )}
                {editable && !isFiltering ? (
                    <span className={styles.extractionSchemaRowActions}>
                        <button
                            type="button"
                            className={styles.extractionSchemaRowAction}
                            disabled={!canMoveUp}
                            onClick={(event) => {
                                event.stopPropagation();
                                actions.moveField(serializedObjectPath, propertyKey, -1);
                            }}
                            aria-label={`Move ${displayName} up`}
                            title="Move up"
                            data-test-id={`extraction-schema-move-up-${pathId}`}
                        >
                            <Icon type="fa" name="chevron-up" aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            className={styles.extractionSchemaRowAction}
                            disabled={!canMoveDown}
                            onClick={(event) => {
                                event.stopPropagation();
                                actions.moveField(serializedObjectPath, propertyKey, 1);
                            }}
                            aria-label={`Move ${displayName} down`}
                            title="Move down"
                            data-test-id={`extraction-schema-move-down-${pathId}`}
                        >
                            <Icon type="fa" name="chevron-down" aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            className={styles.extractionSchemaRemove}
                            onClick={(event) => {
                                event.stopPropagation();
                                actions.removeField(serializedObjectPath, propertyKey, displayName);
                            }}
                            aria-label={`Remove ${displayName}`}
                            title={`Remove ${displayName}`}
                            data-test-id={`extraction-schema-remove-${pathId}`}
                        >
                            <Icon type="fa" name="trash" aria-hidden="true" />
                        </button>
                    </span>
                ) : null}
            </div>
            {editable && isActive ? (
                <FieldEditorCard
                    pathId={pathId}
                    propertyKey={propertyKey}
                    serializedObjectPath={serializedObjectPath}
                    serializedPropertyPath={serializedPropertyPath}
                    title={title}
                    description={description}
                    valueType={valueType}
                    arrayDepth={arrayDepth}
                    required={required}
                    displayName={displayName}
                    actions={actions}
                />
            ) : null}
            {children}
        </li>
    );
}

export default React.memo(FieldRow);
